import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { businessService } from '@/services/business.service'
import { regionService } from '@/services/region.service'
import { useConfirm } from '@/hooks/useConfirm'
import { BusinessStatus } from '@shared/types/entities'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix for default marker icon
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

const defaultIcon = new Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})

export function ScoutBusinessListPage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { confirm, modal: confirmModal } = useConfirm()

  const [selectedStateId, setSelectedStateId] = useState<string>('')
  const [selectedRegionId, setSelectedRegionId] = useState<string>('')
  const [selectedCityId, setSelectedCityId] = useState<string>('')
  const [selectedAreaId, setSelectedAreaId] = useState<string>('')
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list')

  // Fetch states (Bundesländer)
  const { data: statesData } = useQuery({
    queryKey: ['states'],
    queryFn: () => regionService.listStates(token!),
    enabled: !!token,
  })

  // Fetch regions for selected state
  const { data: regionsData } = useQuery({
    queryKey: ['regions', selectedStateId],
    queryFn: () => regionService.listRegions(selectedStateId, token!),
    enabled: !!token && !!selectedStateId,
  })

  // Fetch cities for selected region
  const { data: citiesData } = useQuery({
    queryKey: ['cities', selectedRegionId],
    queryFn: () => regionService.listCities({ regionId: selectedRegionId }, token!),
    enabled: !!token && !!selectedRegionId,
  })

  // Fetch areas for selected city
  const { data: areasData } = useQuery({
    queryKey: ['areas', selectedCityId],
    queryFn: () => regionService.listAreas({ cityId: selectedCityId, activeOnly: true }, token!),
    enabled: !!token && !!selectedCityId,
  })

  // Fetch available businesses with pagination
  const {
    data: businessesData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['businesses', selectedAreaId, BusinessStatus.AVAILABLE],
    queryFn: ({ pageParam }) =>
      businessService.listBusinesses(
        { areaId: selectedAreaId, status: BusinessStatus.AVAILABLE, nextToken: pageParam },
        token!
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: any) => lastPage.nextToken || undefined,
    enabled: !!token && !!selectedAreaId,
  })

  // Reserve mutation
  const reserveMutation = useMutation({
    mutationFn: (businessId: string) => businessService.reserveBusiness(businessId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
      queryClient.invalidateQueries({ queryKey: ['my-businesses'] })
    },
  })

  const states = ((statesData as any)?.states as any[]) || []
  const regions = ((regionsData as any)?.regions as any[]) || []
  const cities = ((citiesData as any)?.cities as any[]) || []
  const areas = ((areasData as any)?.areas as any[]) || []
  const businesses = businessesData?.pages.flatMap((page: any) => page.businesses || []) || []
  const totalCount = businessesData?.pages[0]?.totalCount as number | undefined

  // Infinite scroll with Intersection Observer
  const loadMoreRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) fetchNextPage()
      },
      { threshold: 0.1 }
    )
    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Calculate map center
  const mapCenter =
    businesses.length > 0
      ? [businesses[0].latitude, businesses[0].longitude]
      : [49.0069, 8.4037] // Karlsruhe default

  const handleReserve = async (businessId: string, businessName: string) => {
    const ok = await confirm({
      title: t('scout.reserveBusiness'),
      message: `${businessName} - ${t('scout.confirmReserve')}`,
      confirmLabel: t('scout.reserve'),
      cancelLabel: t('common.cancel'),
    })
    if (ok) {
      reserveMutation.mutate(businessId)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('scout.availableBusinesses')}</h1>
            <p className="text-gray-600">{t('scout.selectArea')}</p>
          </div>
          <button
            onClick={() => navigate('/scout/dashboard')}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            &larr; {t('scout.dashboard')}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.selectState')}
              </label>
              <select
                value={selectedStateId}
                onChange={(e) => {
                  setSelectedStateId(e.target.value)
                  setSelectedRegionId('')
                  setSelectedCityId('')
                  setSelectedAreaId('')
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">{t('admin.selectState')}</option>
                {states.map((state: any) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.selectRegion')}
              </label>
              <select
                value={selectedRegionId}
                onChange={(e) => {
                  setSelectedRegionId(e.target.value)
                  setSelectedCityId('')
                  setSelectedAreaId('')
                }}
                disabled={!selectedStateId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">{t('admin.selectRegion')}</option>
                {regions.map((region: any) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.selectCity')}
              </label>
              <select
                value={selectedCityId}
                onChange={(e) => {
                  setSelectedCityId(e.target.value)
                  setSelectedAreaId('')
                }}
                disabled={!selectedRegionId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">{t('admin.selectCity')}</option>
                {cities.map((city: any) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('admin.selectArea')}
              </label>
              <select
                value={selectedAreaId}
                onChange={(e) => setSelectedAreaId(e.target.value)}
                disabled={!selectedCityId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">{t('admin.selectArea')}</option>
                {areas.map((area: any) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.viewLabel')}</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                    viewMode === 'list'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('common.list')}
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                    viewMode === 'map'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('common.map')}
                </button>
              </div>
            </div>
          </div>

          {selectedAreaId && (
            <div className="mt-4 text-sm text-gray-600">
              {isLoading ? (
                <span>{t('common.loading')}</span>
              ) : (
                <span>
                  {t('scout.businessesFound')}: <strong>{businesses.length}</strong>
                  {totalCount !== undefined && totalCount > businesses.length && (
                    <span> / {totalCount}</span>
                  )}
                  {totalCount !== undefined && totalCount === businesses.length && (
                    <span> ({t('common.total')}: {totalCount})</span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {!selectedAreaId ? (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            {t('scout.selectAreaFirst')}
          </div>
        ) : isLoading ? (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            {t('common.loading')}
          </div>
        ) : businesses.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            {t('scout.noBusinessesInArea')}
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business: any) => (
                <div key={business.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{business.name}</h3>
                    <p className="text-sm text-gray-600 mb-1">{business.address}</p>
                    <p className="text-sm text-gray-600 mb-2">
                      {business.city}, {business.postalCode}
                    </p>
                    {business.category && (
                      <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded mb-4">
                        {business.category}
                      </span>
                    )}
                    <button
                      onClick={() => handleReserve(business.id, business.name)}
                      disabled={reserveMutation.isPending}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {reserveMutation.isPending ? t('common.loading') : t('scout.reserve')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div ref={loadMoreRef} className="mt-6 text-center py-4">
              {isFetchingNextPage && (
                <span className="text-sm text-gray-500">{t('common.loading')}</span>
              )}
            </div>
          </div>
        ) : (
          /* Map View */
          <div className="bg-white rounded-lg shadow overflow-hidden" style={{ height: '600px' }}>
            <MapContainer
              center={mapCenter as [number, number]}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {businesses.map((business: any) => (
                <Marker
                  key={business.id}
                  position={[business.latitude, business.longitude]}
                  icon={defaultIcon}
                >
                  <Popup>
                    <div className="p-2 min-w-[180px]">
                      <h3 className="font-semibold text-gray-900 mb-1">{business.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{business.address}</p>
                      {business.category && (
                        <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded mb-2">
                          {business.category}
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleReserve(business.id, business.name) }}
                        onMouseDown={(e) => e.stopPropagation()}
                        disabled={reserveMutation.isPending}
                        className="block w-full mt-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {t('scout.reserve')}
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        )}
      </div>
      {confirmModal}
    </div>
  )
}
